import React from 'react';
import { AlertTriangle, Home, ArrowLeft } from 'lucide-react';
import { colors, spacing, typography, borders } from '../../tokens';

interface ErrorpageProps {
  errorCode?: number;
  errorTitle?: string;
  errorMessage?: string;
  onGoHome?: () => void;
  onGoBack?: () => void;
}

const Errorpage: React.FC<ErrorpageProps> = ({
  errorCode = 404,
  errorTitle = 'Page Not Found',
  errorMessage = 'The page you are looking for does not exist or has been moved.',
  onGoHome,
  onGoBack,
}) => {
  const [isHomeHovered, setIsHomeHovered] = React.useState(false);
  const [isBackHovered, setIsBackHovered] = React.useState(false);

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: colors.background.primary.default,
      padding: spacing.xl,
    }}>
      <div style={{
        maxWidth: '600px',
        textAlign: 'center',
      }}>
        {/* Error Icon */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: spacing.xl,
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            background: `${colors.status.error.base}10`,
            border: `2px solid ${colors.status.error.base}`,
          }}>
            <AlertTriangle
              size={60}
              color={colors.status.error.base}
            />
          </div>
        </div>

        {/* Error Code */}
        <div style={{
          fontSize: '96px',
          fontWeight: typography.fontWeight.bold,
          color: colors.brand.primary.base,
          lineHeight: 1,
          marginBottom: spacing.lg,
        }}>
          {errorCode}
        </div>

        {/* Error Title */}
        <h1 style={{
          fontSize: typography.fontSize['3xl'],
          fontWeight: typography.fontWeight.bold,
          color: colors.text.primary.default,
          marginBottom: spacing.md,
        }}>
          {errorTitle}
        </h1>

        {/* Error Message */}
        <p style={{
          fontSize: typography.fontSize.lg,
          color: colors.text.secondary.default,
          lineHeight: typography.lineHeight.relaxed,
          marginBottom: spacing.xl,
        }}>
          {errorMessage}
        </p>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: spacing.md,
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}>
          {/* Go Back Button */}
          <button
            onClick={onGoBack || (() => window.history.back())}
            onMouseEnter={() => setIsBackHovered(true)}
            onMouseLeave={() => setIsBackHovered(false)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: spacing.sm,
              padding: `${spacing.md} ${spacing.lg}`,
              background: isBackHovered
                ? colors.background.tertiary.default
                : colors.background.secondary.default,
              color: colors.text.primary.default,
              border: `1px solid ${colors.border.default.default}`,
              borderRadius: borders.component.button.radius,
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.medium,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <ArrowLeft size={18} />
            Go Back
          </button>

          {/* Go Home Button */}
          <button
            onClick={onGoHome || (() => window.location.href = '/')}
            onMouseEnter={() => setIsHomeHovered(true)}
            onMouseLeave={() => setIsHomeHovered(false)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: spacing.sm,
              padding: `${spacing.md} ${spacing.lg}`,
              background: isHomeHovered
                ? `linear-gradient(135deg, ${colors.brand.purple.dark} 0%, ${colors.brand.purple.base} 100%)`
                : `linear-gradient(135deg, ${colors.brand.purple.base} 0%, ${colors.brand.purple.dark} 100%)`,
              color: colors.text.primary.default,
              border: 'none',
              borderRadius: borders.component.button.radius,
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.semibold,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: isHomeHovered
                ? `0 0 30px ${colors.brand.purple.base}50`
                : `0 0 20px ${colors.brand.purple.base}30`,
            }}
          >
            <Home size={18} />
            Go Home
          </button>
        </div>

        {/* Additional Help Text */}
        <div style={{
          marginTop: spacing.xl,
          padding: spacing.lg,
          background: colors.background.secondary.default,
          borderRadius: borders.component.default.radius,
          border: `1px solid ${colors.border.default.default}`,
        }}>
          <p style={{
            fontSize: typography.fontSize.sm,
            color: colors.text.secondary.default,
            margin: 0,
          }}>
            If you believe this is a mistake, please contact support or try refreshing the page.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Errorpage;
